/**
 * GssPeopleSection
 * ----------------
 * Post-session people list for registered GSS classmates (UX §6).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonSpinner,
  ButtonText,
  Heading,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';

import ContentGroup from '@/common/components/ContentGroup';
import { PersonRow, peerInitials } from '@/common/components/people/PersonRow';
import type { PersonRowState } from '@/common/components/people/PersonRow';
import { useAuthStore } from '@/common/auth/store';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useGetGssOccasionPeople } from '@/common/api/endpoints/connections/connections';
import { useGetPeopleProfile } from '@/common/api/endpoints/users/users';
import type { GssOccasionPersonTeaser } from '@/common/api/models/gssOccasionPersonTeaser';
import { useStudentPeopleNavigation } from '@/student/people/StudentPeopleNavigationContext';
import { useConnectionActions } from '@/student/people/hooks/useConnectionActions';

const SKELETON_FALLBACK_MS = 10_000;

type GssPeopleSectionProps = {
  slotId: string;
};

function SkeletonRows() {
  return (
    <VStack space="md" testID="gss-people-skeleton">
      {[0, 1].map((key) => (
        <HStack key={key} space="md" alignItems="center" py="$3">
          <Box w={40} h={40} borderRadius="$full" bg="$bgHover" />
          <VStack flex={1} space="xs">
            <Box h={16} w="40%" borderRadius="$sm" bg="$bgHover" />
            <Box h={12} w="55%" borderRadius="$sm" bg="$bgHover" />
          </VStack>
        </HStack>
      ))}
    </VStack>
  );
}

function mapServerConnectionState(
  teaser: GssOccasionPersonTeaser,
): 'none' | 'pendingIn' | 'pendingOut' | 'connected' {
  if (teaser.connectionDirection === 'pending_in') return 'pendingIn';
  if (teaser.connectionDirection === 'pending_out') return 'pendingOut';
  if (teaser.connectionDirection === 'accepted') return 'connected';
  return 'none';
}

type OccasionPersonRowProps = {
  teaser: GssOccasionPersonTeaser;
  slotId: string;
  actions: ReturnType<typeof useConnectionActions>;
  onViewContact: () => void;
};

function OccasionPersonRow({
  teaser, slotId, actions, onViewContact,
}: OccasionPersonRowProps) {
  const rowTestId = `gss-person-${teaser.userId}`;
  const serverState = mapServerConnectionState(teaser);
  const displayState = actions.getOccasionDisplayState(teaser.userId, serverState);
  const connectionId = teaser.connectionId ?? undefined;

  return (
    <PersonRow
      firstName={teaser.firstName}
      initials={peerInitials(teaser.firstName)}
      photoUrl={teaser.profilePhotoUrl}
      state={displayState as PersonRowState}
      errorMessage={actions.getOccasionRowError(teaser.userId)}
      rowTestId={rowTestId}
      isActionPending={
        actions.isOccasionRowPending(teaser.userId)
        || (!!connectionId && actions.isRowPending(connectionId))
      }
      onConnect={() => actions.requestFromOccasion(teaser.userId, slotId)}
      onAccept={
        connectionId
          ? () => actions.accept(connectionId, {
            acceptedToastName: teaser.firstName,
            onViewContact,
          })
          : undefined
      }
      onDecline={connectionId ? () => actions.decline(connectionId) : undefined}
      onWithdraw={connectionId ? () => actions.withdraw(connectionId) : undefined}
      onViewContact={onViewContact}
      onRetry={() => actions.retryOccasionRow(teaser.userId, slotId)}
    />
  );
}

export function GssPeopleSection({ slotId }: GssPeopleSectionProps) {
  const { t } = useTranslation();
  const peopleNav = useStudentPeopleNavigation();
  const connectionActions = useConnectionActions();
  const [gateDismissed, setGateDismissed] = React.useState(false);
  const [forceRetry, setForceRetry] = React.useState(false);

  const userId = useAuthStore((s) => s.user?.id ?? '');
  const { isFeatureActive } = useUserFeatures();
  const connectionsEnabled = isFeatureActive('student-connections');
  // Same orval key as Profile (`user.id`, never `'me'`) so a save updates this gate.
  const profileQuery = useGetPeopleProfile(userId, {
    query: { enabled: connectionsEnabled && !!userId },
  });
  const occasionQuery = useGetGssOccasionPeople(slotId, {
    query: { enabled: connectionsEnabled && !!slotId, retry: false },
  });

  React.useEffect(() => {
    if (!occasionQuery.isLoading) {
      setForceRetry(false);
    }
  }, [occasionQuery.isLoading, occasionQuery.dataUpdatedAt]);

  React.useEffect(() => {
    if (!occasionQuery.isLoading || occasionQuery.isError) return undefined;
    const timer = setTimeout(() => setForceRetry(true), SKELETON_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [occasionQuery.isLoading, occasionQuery.isError, slotId]);

  if (!connectionsEnabled) {
    return null;
  }

  if (gateDismissed) {
    return null;
  }

  if (occasionQuery.isError) {
    return (
      <ContentGroup variant="subtle" bg="$statusErrorSubtle" testID="gss-people-error">
        <VStack space="md">
          <Text size="md" color="$textOnErrorSubtle" fontWeight="medium">
            {t('people.gss.errorTitle')}
          </Text>
          <Text size="sm" color="$textOnErrorSubtle">
            {t('people.gss.errorBody')}
          </Text>
          <Button
            variant="outline"
            testID="gss-people-try-again"
            onPress={() => occasionQuery.refetch()}
            isDisabled={occasionQuery.isFetching}
          >
            {occasionQuery.isFetching ? <ButtonSpinner /> : null}
            <ButtonText>{t('people.gss.tryAgain')}</ButtonText>
          </Button>
        </VStack>
      </ContentGroup>
    );
  }

  const showSkeleton = occasionQuery.isLoading && !forceRetry;
  if (showSkeleton) {
    return (
      <VStack space="lg" testID="gss-people-loading">
        <SkeletonRows />
      </VStack>
    );
  }

  const payload = occasionQuery.data?.data?.data;
  const profile = profileQuery.data?.data?.data ?? undefined;

  if (forceRetry && occasionQuery.isLoading) {
    return (
      <ContentGroup variant="subtle" bg="$statusErrorSubtle" testID="gss-people-error">
        <VStack space="md">
          <Text size="md" color="$textOnErrorSubtle" fontWeight="medium">
            {t('people.gss.errorTitle')}
          </Text>
          <Text size="sm" color="$textOnErrorSubtle">
            {t('people.gss.errorBody')}
          </Text>
          <Button
            variant="outline"
            testID="gss-people-try-again"
            onPress={() => occasionQuery.refetch()}
            isDisabled={occasionQuery.isFetching}
          >
            {occasionQuery.isFetching ? <ButtonSpinner /> : null}
            <ButtonText>{t('people.gss.tryAgain')}</ButtonText>
          </Button>
        </VStack>
      </ContentGroup>
    );
  }

  if (!payload) {
    return (
      <ContentGroup variant="subtle" bg="$statusErrorSubtle" testID="gss-people-error">
        <VStack space="md">
          <Text size="md" color="$textOnErrorSubtle" fontWeight="medium">
            {t('people.gss.errorTitle')}
          </Text>
          <Text size="sm" color="$textOnErrorSubtle">
            {t('people.gss.errorBody')}
          </Text>
          <Button
            variant="outline"
            testID="gss-people-try-again"
            onPress={() => occasionQuery.refetch()}
            isDisabled={occasionQuery.isFetching}
          >
            {occasionQuery.isFetching ? <ButtonSpinner /> : null}
            <ButtonText>{t('people.gss.tryAgain')}</ButtonText>
          </Button>
        </VStack>
      </ContentGroup>
    );
  }

  // Occasion payload is canonical for G-5: identities in `people` means the
  // server already passed shareable-contact. Do not re-derive from profile GET
  // (a failed/empty profile would false-gate classmates already on the wire).
  const identitiesVisible = (payload.people?.length ?? 0) > 0;
  const showGate = !identitiesVisible && (payload.eligibleCount ?? 0) > 0;
  if (showGate) {
    return (
      <ContentGroup variant="subtle" testID="gss-people-gate">
        <VStack space="md">
          <Heading size="lg">{t('people.gss.gate.title')}</Heading>
          <Text size="sm" color="$textSubtle">
            {t('people.gss.gate.body', { count: payload.eligibleCount })}
          </Text>
          <VStack space="sm">
            <HStack space="sm" alignItems="center">
              <Box w={8} h={8} borderRadius="$full" bg="$statusError" />
              <Text size="sm">{t('people.gss.gate.needContact')}</Text>
              <Text size="xs" color="$statusError">{t('people.gss.gate.required')}</Text>
            </HStack>
            <HStack space="sm" alignItems="center">
              <Box w={8} h={8} borderRadius="$full" bg="$borderMedium" />
              <Text size="sm">{t('people.gss.gate.needPhoto')}</Text>
              <Text size="xs" color="$textSubtle">{t('people.gss.gate.optional')}</Text>
            </HStack>
          </VStack>
          <HStack space="md">
            <Button
              action="primary"
              testID="gss-people-gate-open-profile"
              onPress={() => peopleNav?.navigateToProfile()}
            >
              <ButtonText>{t('people.gss.gate.openProfile')}</ButtonText>
            </Button>
            <Button
              variant="link"
              testID="gss-people-gate-not-now"
              onPress={() => setGateDismissed(true)}
            >
              <ButtonText>{t('people.gss.gate.notNow')}</ButtonText>
            </Button>
          </HStack>
        </VStack>
      </ContentGroup>
    );
  }

  if ((payload.people?.length ?? 0) === 0) {
    const optedIn = payload.viewerOpenToConnect !== false;
    return (
      <ContentGroup variant="subtle" testID="gss-people-empty">
        <VStack space="md">
          <Text size="md">{t('people.gss.emptyIn')}</Text>
          <Text size="sm" color="$textSubtle">
            {optedIn ? t('people.gss.emptyInBody') : t('people.gss.emptyOutBody')}
          </Text>
          {!optedIn ? (
            <Button
              variant="outline"
              testID="gss-people-empty-open-profile"
              onPress={() => peopleNav?.navigateToProfile()}
            >
              <ButtonText>{t('people.gss.openProfile')}</ButtonText>
            </Button>
          ) : null}
        </VStack>
      </ContentGroup>
    );
  }

  return (
    <VStack space="lg" testID="gss-people-list">
      <VStack space="xs">
        <Heading size="lg">{t('people.gss.title')}</Heading>
        <Text size="sm" color="$textSubtle">{t('people.gss.subtitle')}</Text>
        {identitiesVisible && profile && !profile.profilePhotoBlobPath ? (
          <Text size="sm" color="$textSubtle" testID="gss-people-photo-note">
            {t('people.gss.gate.photoNote')}
          </Text>
        ) : null}
      </VStack>
      <VStack>
        {payload.people.map((person) => (
          <OccasionPersonRow
            key={person.userId}
            teaser={person}
            slotId={slotId}
            actions={connectionActions}
            onViewContact={() => peopleNav?.navigateToConnections()}
          />
        ))}
      </VStack>
    </VStack>
  );
}
